// src/pages/LoginPage.tsx

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authApi } from '../api';

interface Props { resetMode?: boolean; }

export default function LoginPage({ resetMode }: Props) {
  const { login } = useAuth();
  const navigate  = useNavigate();

  const [view, setView]         = useState<'login' | 'forgot' | 'reset'>(
    resetMode ? 'reset' : 'login'
  );
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail]       = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [success, setSuccess]   = useState('');

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await login(username.trim(), password);
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await authApi.forgotPassword(email.trim());
      setSuccess('If that email exists, a reset link has been sent.');
    } catch {
      setSuccess('If that email exists, a reset link has been sent.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(''); setLoading(true);
    const params = new URLSearchParams(window.location.search);
    const userId = params.get('userId') || '';
    const token  = params.get('token') || '';
    try {
      await authApi.resetPassword(userId, token, newPassword);
      setSuccess('Password reset. You can now log in.');
      setTimeout(() => navigate('/login'), 2000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Reset failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh', background: 'var(--bg)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{ width: 360 }}>

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div style={{
            fontFamily: 'var(--font-mono)', fontSize: 28,
            fontWeight: 500, color: 'var(--green)', letterSpacing: -1,
          }}>
            habit<span style={{ color: 'var(--text3)' }}>.</span>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 6 }}>
            {view === 'login' ? 'Sign in to continue' :
             view === 'forgot' ? 'Reset your password' :
             'Choose a new password'}
          </div>
        </div>

        {/* Card */}
        <div className="card">
          {error && (
            <div style={{
              padding: '10px 12px', borderRadius: 'var(--radius-sm)',
              background: 'var(--red-dim)', border: '1px solid rgba(255,94,125,0.2)',
              color: 'var(--red)', fontSize: 12, marginBottom: 16,
            }}>
              {error}
            </div>
          )}
          {success && (
            <div style={{
              padding: '10px 12px', borderRadius: 'var(--radius-sm)',
              background: 'var(--green-dim)', border: '1px solid rgba(0,229,160,0.2)',
              color: 'var(--green)', fontSize: 12, marginBottom: 16,
            }}>
              {success}
            </div>
          )}

          {/* LOGIN */}
          {view === 'login' && (
            <form onSubmit={handleLogin}>
              <Field label="Username" value={username} onChange={setUsername}
                type="text" placeholder="your username" autoFocus />
              <Field label="Password" value={password} onChange={setPassword}
                type="password" placeholder="••••••••••••" />
              <SubmitBtn loading={loading}>Sign in</SubmitBtn>
              <button type="button" onClick={() => { setView('forgot'); setError(''); }}
                style={{ width:'100%', marginTop:12, padding:'8px', background:'transparent',
                  border:'none', color:'var(--text3)', fontSize:11, cursor:'pointer' }}>
                Forgot password?
              </button>
            </form>
          )}

          {/* FORGOT */}
          {view === 'forgot' && (
            <form onSubmit={handleForgot}>
              <Field label="Email address" value={email} onChange={setEmail}
                type="email" placeholder="your@email.com" autoFocus />
              <SubmitBtn loading={loading}>Send reset link</SubmitBtn>
              <button type="button" onClick={() => { setView('login'); setError(''); setSuccess(''); }}
                style={{ width:'100%', marginTop:12, padding:'8px', background:'transparent',
                  border:'none', color:'var(--text3)', fontSize:11, cursor:'pointer' }}>
                ← Back to sign in
              </button>
            </form>
          )}

          {/* RESET */}
          {view === 'reset' && (
            <form onSubmit={handleReset}>
              <Field label="New password" value={newPassword} onChange={setNewPassword}
                type="password" placeholder="12+ characters" autoFocus />
              <div style={{ fontSize:10, color:'var(--text3)', marginBottom:16 }}>
                Must include uppercase, lowercase, number, and special character.
              </div>
              <SubmitBtn loading={loading}>Set new password</SubmitBtn>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type, placeholder, autoFocus }: {
  label: string; value: string; onChange: (v: string) => void;
  type: string; placeholder?: string; autoFocus?: boolean;
}) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display:'block', fontSize:11, color:'var(--text3)', marginBottom:5,
        fontFamily:'var(--font-mono)', letterSpacing:'0.3px' }}>
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        autoFocus={autoFocus}
        required
        style={{
          width:'100%', padding:'10px 12px',
          background:'var(--bg3)', border:'1px solid var(--border2)',
          borderRadius:'var(--radius-sm)', color:'var(--text)', fontSize:13,
          outline:'none', transition:'border-color 0.15s',
        }}
        onFocus={e => e.target.style.borderColor = 'var(--green)'}
        onBlur={e  => e.target.style.borderColor = 'var(--border2)'}
      />
    </div>
  );
}

function SubmitBtn({ children, loading }: { children: React.ReactNode; loading: boolean }) {
  return (
    <button type="submit" disabled={loading} style={{
      width:'100%', padding:'11px',
      background: loading ? 'var(--green-dim)' : 'var(--green)',
      border:'none', borderRadius:'var(--radius-sm)',
      color: loading ? 'var(--green)' : '#0c0e14',
      fontSize:13, fontWeight:600, cursor: loading ? 'not-allowed' : 'pointer',
      transition:'all 0.15s', fontFamily:'var(--font-ui)',
    }}>
      {loading ? 'Please wait...' : children}
    </button>
  );
}
